import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CircularProgress, Grid } from '@mui/material'
import { usePasswordForm, passwordValidation } from '@emerald-works-nova/auth'
import { FormInputRow, ActionButton } from '@emerald-works-nova/components'
import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import { TenantEditSlice, UserUploadSlice } from '../../../../reducers'
import { BannerStatic } from '../../../../components'
import validator from './validator'
import Header from '../header'
import UploadInput from './upload-input'
import PasswordInput from './password-input'
import ToggleInput from './toggle-input'
import { SelectInput } from './select-input'

const requiredKeys = ['s3Key', 'password']

const passwordOptions = {
  defaultPassword: {
    key: 'default',
    value: 'Default Password'
  },
  specificPassword: {
    key: 'specific',
    value: 'Specific Password'
  }
}

const UserUpload = ({ onClickCancel, onUploadStart }) => {
  const [uploadedAt, setUploadedAt] = useState(0)
  const { userPoolId, toolkitType, loginProviders } = useSelector(TenantEditSlice.selectors.tenant)
  const isSSOEnabled = loginProviders?.some(
    lp => lp.providerType === 'SAML' && lp.providerName !== 'internal'
  )

  const checkFileExpiration = (timestamp) => {
    const date = new Date(timestamp)
    const currentDate = new Date()
    const expiresAt = new Date(date.setMinutes(date.getMinutes() + 12))

    return currentDate.getTime() > expiresAt.getTime()
  }

  const [userUploadObjectCreated] = useEvent([
    {
      eventName: 'userUploadObjectCreated',
      onSuccess: () => {
        setUploadedAt(Date.now())
      }
    }])

  const [userUploadStart, userUploadFinished] = useEvent([
    UserUploadSlice.eventBus.userUploadStart,
    UserUploadSlice.eventBus.userUploadFinished,
    UserUploadSlice.eventBus.userUploadProgress
  ])

  useEventsOnViewLoad(() => {
    userUploadStart.registerAdhocOnSuccessListener(() => onUploadStart())
    userUploadStart.registerAdhocOnErrorListener(e => {
      console.log('userUploadStart.registerAdhocOnErrorListener', e)
      const label = `Error starting user upload: ${e?.message ?? e?.name}`
      BannerStatic.show({ label, isError: true })
    })
  }, [userUploadStart])

  useEventsOnViewLoad(() => {
    userUploadFinished.registerAdhocOnSuccessListener(
      ({ payload: { tenant, result } }) => {
        const hasErrors = result.errors > 0
        const banner = hasErrors ? {
          label: `Bulk Upload of users at ${tenant.clientName ?? tenant.name} has finished with errors`,
          isWarning: true
        } : {
          label: `Bulk Upload of users at ${tenant.clientName ?? tenant.name} has finished`
        }
        BannerStatic.show(banner)
      }
    )
    userUploadFinished.registerAdhocOnErrorListener(e => {
      console.log('userUploadFinished.registerAdhocOnErrorListener', e)
      BannerStatic.show({ label: 'Error while uploading users', isError: true })
    })
  }, [userUploadFinished])

  const isLoading = userUploadStart.isWorking

  const form = usePasswordForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      s3Key: '',
      password: '',
      passwordOption: passwordOptions.defaultPassword.key,
      enablePasswordReset: true,
      enableWeeklyEmail: true
    },
    passwordValidation
  })

  const { control, handleSubmit, setValue, watch, reset, completed } = form

  const onSubmit = data => {
    if (uploadedAt && checkFileExpiration(uploadedAt)) {
      return BannerStatic.show({
        label: 'File has expired. Please re-upload the file again.',
        isWarning: true
      })
    }

    userUploadStart.trigger({ ...data, userPoolId })
  }

  const handleClickCancel = () => {
    reset()
    onClickCancel()
  }

  const handleFileAccept = ({ s3Key }) => {
    userUploadObjectCreated.trigger({ s3Key })
    setValue('s3Key', s3Key)
  }

  const values = watch()
  const isValid =
    requiredKeys.every(key => !!values[key]) && completed('password')

  const uploadDescription = useMemo(() => {
    if (values.passwordOption === passwordOptions.specificPassword.key) {
      return `Upload a CSV UTF-8 (Comma delimited) file that contains a First Name, Last Name, Email or Username (required), NameID${isSSOEnabled ? ' (required)' : ''}, and Password (required)`
    }
    return `Upload a CSV UTF-8 (Comma delimited) file that contains a First Name, Last Name, Email or Username (required), and NameID${isSSOEnabled ? ' (required)' : ''}`
  }, [values.passwordOption, isSSOEnabled])

  const onValidation = useCallback(async (file) => {
    const requiredFields = []

    if (isSSOEnabled) {
      requiredFields.push('NameID')
    }

    if (values.passwordOption === passwordOptions.specificPassword.key) {
      requiredFields.push('Password')
    }

    const rules = {
      requiredFields,
      identifiers: ['Email', 'Username']
    }

    const result = await validator(file, rules)

    if (result.code) {
      return result
    }
  }, [values.passwordOption, isSSOEnabled])

  return (
    <Grid container>
      <Header
        title='Upload Users'
        actions={
          <>
            <Grid item>
              <ActionButton
                variant='secondary'
                dataTest='button-cancel'
                onClick={handleClickCancel}
                disabled={isLoading}
              >
                Cancel
              </ActionButton>
            </Grid>
            <Grid item>
              <ActionButton
                variant='primary'
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || userUploadObjectCreated.isWorking}
                isWorking={isLoading}
                dataTest='button-upload-users'
                startIcon={userUploadObjectCreated.isWorking && (<CircularProgress size={15} />)}
              >
                Upload Users
              </ActionButton>
            </Grid>
          </>
        }
      />
      {toolkitType !== 'SMALL_BUSINESS_TOOLKIT' && (
        <Grid item xs={12}>
          <FormInputRow
            title='Password Option'
            description='Select Default Password to manually enter a single password for all entries, or Specific Password to upload a CSV file with passwords for each entry'
            smallFormTitles
            InputComponent={<SelectInput control={control} name='passwordOption' id='password-option' label='Password Option' items={Object.values(passwordOptions)} />}
          />
        </Grid>)}
      {values.passwordOption === passwordOptions.defaultPassword.key && (
        <>
          <Grid item xs={12}>
            <FormInputRow
              title='Default Password'
              description='Add a password that contains a minimum of eight characters with at least one lowercase, one uppercase, one special character, and one numeral'
              smallFormTitles
              InputComponent={<PasswordInput {...form} />}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInputRow
              title='Enable Password Reset on First Login'
              description='Users will be asked to set a new password on first login'
              smallFormTitles
              InputComponent={
                <ToggleInput
                  name='enablePasswordReset'
                  label='Enable password reset'
                  control={control}
                />
              }
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <FormInputRow
          title='Enable the Weekly Email?'
          description="Keep the user's subscription on the mail list"
          smallFormTitles
          InputComponent={
            <ToggleInput
              name='enableWeeklyEmail'
              label='Enable weekly email'
              control={control}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='File Upload'
          description={uploadDescription}
          smallFormTitles
          InputComponent={<UploadInput onAccept={handleFileAccept} onValidation={onValidation} />}
        />
      </Grid>
    </Grid>
  )
}

export default UserUpload
